import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Pagination } from '@/components/common/Pagination';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { useReviews, useReviewFilters } from '../hooks';
import { useReviewColumns, ReviewsDataTable, CreateReviewDialog } from '../components';

export function ReviewsPage() {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const { filters, updateFilter, updateFilters, resetFilters, handleSort } = useReviewFilters();

  const { data, isLoading, isError, error } = useReviews(filters);

  // Zoom is owned here so its control can sit in the Pagination row (left of
  // the per-page select) while the table applies the same value to its rows.
  const tableZoom = useTableZoom();

  // Slot in the title row the table portals its Columns/Filters buttons into.
  const [tableControlsEl, setTableControlsEl] = useState<HTMLDivElement | null>(null);

  const columns = useReviewColumns({
    orderBy: filters.orderBy || '',
    order: filters.order || '',
    onSort: handleSort,
    filters,
  });

  if (isError) {
    return (
      <div className="p-4">
        <p className="text-red-500">{error?.message || 'Error loading reviews'}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b-1">
        <h1 className="text-xl font-semibold">{t('reviews')}</h1>
        <div className="flex items-center gap-3">
          <div ref={setTableControlsEl} className="flex items-center gap-2" />
          {hasAccess('review', 'create') && <CreateReviewDialog />}
        </div>
      </div>
      <div className="px-4 pt-4">
        <ReviewsDataTable
          tableZoom={tableZoom}
          controlsContainer={tableControlsEl}
          data={data?.results || []}
          columns={columns}
          isLoading={isLoading}
          filters={filters}
          updateFilter={updateFilter}
          updateFilters={updateFilters}
          onClearFilters={resetFilters}
        />

        <Pagination
          leadingControls={<TableZoomControls zoom={tableZoom} />}
          page={filters.page}
          limit={filters.limit}
          total={data?.meta?.total ?? data?.total ?? 0}
          onPageChange={(val) => updateFilter('page', val)}
          onLimitChange={(val) => updateFilters({ limit: val, page: 0 })}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
